import Cookies from "js-cookie";
import { useEffect, useMemo, useState } from "react";

import { CloseIcon } from "@/components/icons/close-icon";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import useAuth from "@/hooks/use-auth";
import { useIsBreakPoint } from "@/hooks/use-is-break-point";

const COOKIE_NAME = "app_tmp_banner";
const COOKIE_EXPIRATION = 30;

interface MobileBannerProps {
  isHomePage: boolean;
  isIos: boolean;
}

export default function MobileBanner({ isHomePage, isIos }: MobileBannerProps) {
  const { isLoading, isUser, login, signup } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const isDesktop = useIsBreakPoint("md");

  const handleClose = (open: boolean) => {
    if (open) {
      return;
    }

    Cookies.set(COOKIE_NAME, new Date().toString(), {
      expires: COOKIE_EXPIRATION,
    });
    setIsOpen(false);
  };

  const handleLogin = () => {
    handleClose(false);
    login();
  };

  const handleSignup = () => {
    handleClose(false);
    signup();
  };

  const { body, title } = useMemo(() => {
    if (isIos) {
      return {
        body: "For a faster, more social experience",
        title: "Open in the Morty app",
      };
    }

    return {
      body: "Login to wishlist, track, and rate games",
      title: "Level up your Morty experience",
    };
  }, [isIos]);

  useEffect(() => {
    if (isLoading || isUser || Cookies.get(COOKIE_NAME) || isHomePage) {
      return;
    }

    setIsOpen(true);
  }, [isHomePage, isLoading, isUser]);

  if (isDesktop) {
    return null;
  }

  return (
    <Dialog onOpenChange={handleClose} open={isOpen}>
      <DialogContent
        className="mb-17.5 rounded-none border-white/10 border-t bg-rose-red shadow-none"
        overlayClassName="bg-transparent"
      >
        <DialogHeader className="relative px-4 pt-5">
          <DialogTitle className="font-semibold text-16/[1.4em] text-white">
            {title}
          </DialogTitle>
          <Button
            aria-label="Close"
            className="absolute top-3.5 right-3.5 size-6"
            onClick={() => handleClose(false)}
          >
            <CloseIcon />
          </Button>
        </DialogHeader>
        <p className="px-4 pt-1 pb-6 text-14/[1.4em] text-white">{body}</p>
        <DialogFooter className="flex-row gap-6 px-4 pb-5">
          <Button
            className="h-8.5 w-full rounded-[17px] border border-white/50 font-medium text-14 text-white"
            onClick={handleLogin}
          >
            Log in
          </Button>
          {isIos ? (
            <a
              className="flex h-8.5 w-full items-center justify-center rounded-[17px] bg-black font-medium text-14 text-white"
              href="https://link.morty.app/VAdfzl1F9Ab"
              onClick={() => handleClose(false)}
              rel="noopener noreferrer"
              target="_blank"
            >
              Use app
            </a>
          ) : (
            <Button
              className="h-8.5 w-full rounded-[17px] bg-black font-medium text-14 text-white"
              onClick={handleSignup}
            >
              Create account
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
